import api from 'app/api'

export const groupService = api.injectEndpoints({
  endpoints: (build) => {
    return {
      getChannels: build.query({
        query: () => ({ url: 'group/list/channels' }),
      }),
      getGroupByIdForAdmin: build.query({
        query: ({ groupId }) => ({ url: `group/${groupId}` }),
      }),
      getGroupsForAdmin: build.query({
        query: (args) => {
          const { skip, take, sort, count, filter } = args
          return {
            url: `group/list?$skip=${skip}&$take=${take}&$count=${count}&$filters=${encodeURIComponent(
              JSON.stringify(filter ?? {}),
            )}&$sort=${encodeURIComponent(JSON.stringify(sort ?? {}))}`,
            method: 'GET',
          }
        },
      }),
      getTeamList: build.query({
        query: () => ({url: `group/list/user/teams` }),
      }),
      getChannelTeamList: build.query({
        query: () => ({url: `group/list/user/channel/teams` }),
      }),
      getNewsfeedTeamList: build.query({
        query: () => ({url: `group/list/newsfeed/teams` }),
      }),
      getAdminNewsfeedTeamList: build.query({
        query: (args) => {
          const { skip, take, sort, count = true, filter, teamId= "" } = args
          return {
            url: `group/admin/newsfeed/list/teams/?$skip=${skip}&$take=${take}&$count=${count}&$filters=${encodeURIComponent(
              JSON.stringify(filter ?? {}),
            )}&$sort=${encodeURIComponent(JSON.stringify(sort ?? {}))}&teamId=${teamId}`,
            method: 'GET',
          }
        },
      }),
      getNewsfeedShareTeamList: build.query({
        query: () => ({url: `group/list/newsfeed/share/teams` }),
      }),
      getUserGroupsAndChannelsWithMembersInfo: build.query({
        query: (userId) => ({
          url: `group/list/groups/channels/with/members/info/${userId}`,
        }),
      }),

      getUserGroupsTags: build.query({
        query: (userId) => ({
          url: `group/list/groups/channels/with/tags/${userId}`,
        }),
      }),

      getGroupInfoByGroupId: build.query({
        query: ({ groupId }) => ({ url: `group/${groupId}` }),
      }),
      createGroup: build.mutation({
        query: (args) => ({ url: 'group', method: 'POST', body: args }),
        invalidatesTags: ['CREATE_GROUP'],
      }),
      updateGroup: build.mutation({
        query: (args) => ({ url: `group/${args.id}`, method: 'PUT', body: args.data }),
        invalidatesTags: ['UPDATE_GROUP'],
      }),
      updateGroupNewsfeedTeams: build.mutation({
        query: (args) => ({ url: `group/newsfeed-groups/${args.teamId}/share/teams`, method: 'PUT', body: args.data }),
      }),
      toggleTeamArchive: build.mutation({
        query: ({ groupId }) => ({
          url: `group/${groupId}/archive/toggle`,
          method: 'PUT',
        }),
        invalidatesTags: ['UPDATE_CHANNEL'],
      }),
      createChannel: build.mutation({
        query: (args) => ({ url: 'group/channel', method: 'POST', body: args }),
        invalidatesTags: ['CREATE_CHANNEL'],
      }),
      searchChannels: build.query({
        query: ({ searchText }) => ({
          url: `group/list/channels/search?searchText=${encodeURIComponent(searchText)}`,
        }),
      }),
      removeChannelMember: build.mutation({
        query: ({ channelId: roomId, userId, name }) => ({
          url: `room/${roomId}/members/remove?userId=${userId}`,
          method: 'PUT',
          body: { name },
        }),
        invalidatesTags: ['REMOVE_CHANNEL_MEMBER'],
      }),
      getMembersBySearchForGroup: build.query({
        query: ({ searchName }) => ({
          url: `group/list/members?searchText=${searchName}`,
        }),
      }),
      getMembersBySearchForChannel: build.query({
        query: ({ channelId = '', searchName }) => ({
          url: `group/list/channels/members/search?searchText=${searchName}&roomId=${channelId}`,
        }),
      }),

      getPaginatedChannels: build.query({
        query: (args) => {
          const { skip, take, sort, count, filter } = args
          return {
            url: `group/list/channels/filter?$skip=${skip}&$take=${take}&$count=${count}&$filters=${encodeURIComponent(
              JSON.stringify(filter ?? {}),
            )}&$sort=${encodeURIComponent(JSON.stringify(sort ?? {}))}`,
            method: 'GET',
          }
        },
      }),
      addChannelMember: build.mutation({
        query: ({ channelId: roomId, userId, name }) => ({
          url: `room/${roomId}/members/add?userId=${userId}`,
          method: 'PUT',
          body: { name },
        }),
        invalidatesTags: ['ADD_CHANNEL_MEMBER'],
      }),
      addChannelModerator: build.mutation({
        query: ({ channelId: roomId, userId, name }) => ({
          url: `subscription/${roomId}/${userId}/moderator/assign`,
          method: 'PUT',
          body: { name },
        }),
        invalidatesTags: ['ADD_CHANNEL_MEMBER'],
      }),
      addGroupMember: build.mutation({
        query: ({ groupId, userId, name }) => ({
          url: `group/${groupId}/members/add/${userId}`,
          method: 'PUT',
          body: { name },
        }),
        invalidatesTags: ['ADD_GROUP_MEMBER'],
      }),
      removeGroupMember: build.mutation({
        query: ({ groupId, userId, name }) => ({
          url: `group/${groupId}/members/remove/${userId}`,
          method: 'PUT',
          body: { name },
        }),
        invalidatesTags: ['REMOVE_GROUP_MEMBER'],
      }),
    
      updateUserPermission: build.mutation({
        query: ({ channelId: roomId, key, value, userId }) => ({
          url: `group/channels/${roomId}/members/permission`,
          method: 'PUT',
          body: { key, value, userId },
        }),
        invalidatesTags: ['UPDATE_USER_PERMISSION'],
      }),
      addTeamToNewsfeedGroup: build.mutation({
        query: ({ groupId, teamId }) => ({
          url: `group/newsfeed-groups/${groupId}/add/${teamId}`,
          method: 'POST',
        }),
      }),
      removeTeamFromNewsfeedGroup: build.mutation({
        query: ({ groupId, teamId }) => ({
          url: `group/newsfeed-groups/${groupId}/remove/${teamId}`,
          method: 'POST',
        }),
      }),
      getUserTeamsByAdmin: build.query({
        query: ({ userId }) => ({
          url: `admin/user/teams/list/${userId}`,
        }),
      }),
    }
  },
})

export const {
  useLazyGetTeamListQuery,
  useLazyGetNewsfeedTeamListQuery,
  useLazyGetAdminNewsfeedTeamListQuery,
  useLazyGetNewsfeedShareTeamListQuery,
  useLazyGetChannelsQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useUpdateGroupNewsfeedTeamsMutation,
  useCreateChannelMutation,
  useLazySearchChannelsQuery,
  useLazyGetGroupByIdForAdminQuery,
  useLazyGetGroupsForAdminQuery,
  useAddChannelMemberMutation,
  useAddChannelModeratorMutation,
  useAddGroupMemberMutation,
  useRemoveGroupMemberMutation,
  useRemoveChannelMemberMutation,
  useLazyGetMembersBySearchForChannelQuery,
  useLazyGetMembersBySearchForGroupQuery,
  useUpdateUserPermissionMutation,
  useLazyGetGroupInfoByGroupIdQuery,
  useToggleTeamArchiveMutation,
  useLazyGetPaginatedChannelsQuery,
  useLazyGetUserGroupsAndChannelsWithMembersInfoQuery,
  useLazyGetUserGroupsTagsQuery,
  useRemoveTeamFromNewsfeedGroupMutation,
  useAddTeamToNewsfeedGroupMutation,
  useLazyGetUserTeamsByAdminQuery,
  useLazyGetChannelTeamListQuery
} = groupService
